import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { useSessionComms } from '../../contexts/SessionCommsContext';
import { motion, AnimatePresence } from 'framer-motion';
import BackButton from '../../components/BackButton';
import SettingsButton from '../../components/SettingsButton';
import HelpButton from '../../components/HelpButton';
import SocialButton from '../../components/SocialButton';
import UnoCard from '../../components/uno/UnoCard';
import GameLog from '../../components/GameLog';
import { ShuffleIcon, SkullIcon } from '../../components/icons/AppIcons';
import GameState from '../../logic/uno-nm/GameState';
import Player from '../../logic/uno-nm/Player';
import storageManager from '../../utils/StorageManager';
import { sessionApi } from '../../services/sessionApi';
import { hydrateUnoNmGameState, serializeUnoNmGameState } from '../../utils/unoState';
import {
    consumeSuppressedSessionWrite,
    isIncomingVirtualSnapshotDifferent,
    markRemoteVirtualSnapshotApplied,
    markVirtualSnapshotSignature
} from '../../utils/virtualGameSnapshotSync';
import {
    canUseCachedGameStateInContext,
    clearSessionGameStateCaches,
    stampSessionScopedGameState,
} from '../../utils/sessionGameState';

const UNO_NM_GAME_CACHE_KEY = 'uno_nm_game_state';

function UnoNMGame() {
    const navigate = useNavigate();
    const location = useLocation();
    const { theme } = useTheme();
    const { session, activeSessionContext, emitRealtimeEvent, virtualGameSyncTick } = useSessionComms();
    const isSessionMode = Boolean(activeSessionContext?.code);
    const sessionCode = String(activeSessionContext?.code || '').trim().toUpperCase();
    const sessionAlias = String(activeSessionContext?.alias || '').trim();
    const sessionHostAlias = String(session?.hostAlias || '').trim();
    const isSessionHost = isSessionMode
        && (
            activeSessionContext?.role === 'host'
            || (sessionHostAlias && sessionHostAlias.toLowerCase() === sessionAlias.toLowerCase())
        );
    const sessionGame = isSessionMode && session?.activeVirtualGame?.game === 'uno-nm'
        ? session.activeVirtualGame
        : null;
    const canOpenExitMenu = !isSessionMode || isSessionHost;
    const initialSessionSnapshot = sessionGame?.snapshot || location.state?.__sessionGameSnapshot || null;

    const cachedUnoNmStateRaw = storageManager.getTemporaryData(UNO_NM_GAME_CACHE_KEY) || {};
    const shouldUseCachedUnoNmState = canUseCachedGameStateInContext(cachedUnoNmStateRaw, isSessionMode, sessionCode);
    const hasCachedUnoNmState = Boolean(cachedUnoNmStateRaw && Object.keys(cachedUnoNmStateRaw).length);
    const cachedUnoNmState = shouldUseCachedUnoNmState ? cachedUnoNmStateRaw : {};
    const hasRouteState = Boolean(location.state && Object.keys(location.state).length > 0);
    const setupState = hasRouteState ? (location.state || {}) : (sessionGame?.setup || cachedUnoNmState.setup || {});

    const {
        playerName = 'Player 1',
        numPlayers = 2,
        difficulty = 'easy',
        showCardCount = true,
        playerNames: playerNamesFromSetup = [],
        humanPlayers: humanPlayersFromSetup = [],
        allHumans: allHumansFromSetup = false,
    } = setupState;

    const [game, setGame] = useState(() => (
        initialSessionSnapshot?.serializedGame
            ? hydrateUnoNmGameState(initialSessionSnapshot.serializedGame)
            : (!hasRouteState && cachedUnoNmState.serializedGame
                ? hydrateUnoNmGameState(cachedUnoNmState.serializedGame)
                : null)
    ));
    const [, forceUpdate] = useState(0);
    const [selectedCard, setSelectedCard] = useState(hasRouteState ? null : (cachedUnoNmState.selectedCard || null));
    const [showColorPicker, setShowColorPicker] = useState(
        hasRouteState ? false : Boolean(cachedUnoNmState.showColorPicker)
    );
    const [showPlayerPicker, setShowPlayerPicker] = useState(
        hasRouteState ? false : Boolean(cachedUnoNmState.showPlayerPicker)
    );
    const [showDiscardAllPicker, setShowDiscardAllPicker] = useState(
        hasRouteState ? false : Boolean(cachedUnoNmState.showDiscardAllPicker)
    );
    const [showRouletteColorPicker, setShowRouletteColorPicker] = useState(
        hasRouteState ? false : Boolean(cachedUnoNmState.showRouletteColorPicker)
    );
    const [rouletteDrawnCards, setRouletteDrawnCards] = useState(
        hasRouteState ? [] : (cachedUnoNmState.rouletteDrawnCards || [])
    );
    const [gameMessage, setGameMessage] = useState(
        typeof initialSessionSnapshot?.gameMessage === 'string'
            ? initialSessionSnapshot.gameMessage
            : (hasRouteState ? '' : (cachedUnoNmState.gameMessage || ''))
    );
    const [isAnimating, setIsAnimating] = useState(false);
    const [showUnoMessage, setShowUnoMessage] = useState(false);
    const [unoPlayer, setUnoPlayer] = useState(null);
    const [lastHandSizes, setLastHandSizes] = useState(
        Array.isArray(initialSessionSnapshot?.lastHandSizes)
            ? initialSessionSnapshot.lastHandSizes
            : (hasRouteState ? [] : (cachedUnoNmState.lastHandSizes || []))
    );
    const [needsCpuTurn, setNeedsCpuTurn] = useState(false);
    const [showUnplayableCards, setShowUnplayableCards] = useState(
        hasRouteState ? false : Boolean(cachedUnoNmState.showUnplayableCards)
    );
    const [eliminatedMessage, setEliminatedMessage] = useState('');
    const [showEliminatedMessage, setShowEliminatedMessage] = useState(false);
    const [showHandRotationMessage, setShowHandRotationMessage] = useState(false); // PHASE 3
    const [handRotationDirection, setHandRotationDirection] = useState(''); // PHASE 3
    const [showWinnerCard, setShowWinnerCard] = useState(false);
    const [winnerName, setWinnerName] = useState('');
    const [isEndingFromElimination, setIsEndingFromElimination] = useState(false);
    const [showExitMenu, setShowExitMenu] = useState(false);
    const [gameLog, setGameLog] = useState(
        Array.isArray(initialSessionSnapshot?.gameLog)
            ? initialSessionSnapshot.gameLog
            : (hasRouteState ? [] : (cachedUnoNmState.gameLog || []))
    );
    const lastLoggedMessage = useRef('');
    const cpuTurnLockRef = useRef('');
    const snapshotSignatureRef = useRef('');
    const suppressNextSessionWriteRef = useRef(false);
    const lastOutboundWriteAtRef = useRef(0);
    const lastAppliedRemoteUpdatedAtRef = useRef(0);
    const wasSessionModeRef = useRef(isSessionMode);
    const [lastRevealedByPlayer, setLastRevealedByPlayer] = useState(
        hasRouteState ? {} : (cachedUnoNmState.lastRevealedByPlayer || {})
    );

    useEffect(() => {
        if (shouldUseCachedUnoNmState) return;
        if (!hasCachedUnoNmState) return;
        storageManager.removeTemporaryData(UNO_NM_GAME_CACHE_KEY);
    }, [hasCachedUnoNmState, shouldUseCachedUnoNmState]);

    useEffect(() => {
        if (canOpenExitMenu) return;
        if (!showExitMenu) return;
        setShowExitMenu(false);
    }, [canOpenExitMenu, showExitMenu]);

    const updateGame = () => {
        forceUpdate(n => n + 1);
    };

    const getCpuTurnKey = () => {
        if (!game) return '';
        return [
            game.currentPlayerIndex,
            game.currentColor,
            game.deck?.cards?.length || 0,
            game.discardPile?.length || 0,
            game.isStacking ? 1 : 0,
            game.stackedDrawCount || 0,
            game.rouletteActive ? 1 : 0,
            game.players.map((player) => `${player.name}:${player.hand.length}:${player.isEliminated ? 1 : 0}`).join('|')
        ].join('::');
    };

    const closeColorPicker = () => {
        setShowColorPicker(false);
        setSelectedCard(null);
        setIsAnimating(false);
    };

    const showWinner = (winner) => {
        setWinnerName(winner.name);
        setShowWinnerCard(true);
        setIsAnimating(true);
        setTimeout(() => {
            setShowWinnerCard(false);
            storageManager.removeTemporaryData(UNO_NM_GAME_CACHE_KEY);
            navigate('/uno-nm-setup');
        }, 2200);
    };

    const showElimination = (playerName) => {
        setEliminatedMessage(playerName);
        setShowEliminatedMessage(true);
        setTimeout(() => {
            setShowEliminatedMessage(false);
        }, 3000);
    };

    const endForHumanElimination = (eliminatedPlayerName) => {
        if (showWinnerCard || isEndingFromElimination) {
            return;
        }

        setNeedsCpuTurn(false);
        setGameMessage(`${eliminatedPlayerName} was eliminated. Match over.`);
        setIsAnimating(true);
        setIsEndingFromElimination(true);

        setTimeout(() => {
            storageManager.removeTemporaryData(UNO_NM_GAME_CACHE_KEY);
            navigate('/uno-nm-setup');
        }, 2200);
    };

    // PHASE 3: Show hand rotation popup
    const showHandRotation = (direction) => {
        setHandRotationDirection(direction === 1 ? 'clockwise' : 'counter-clockwise');
        setShowHandRotationMessage(true);
        setTimeout(() => {
            setShowHandRotationMessage(false);
        }, 2500);
    };

    const checkForWinner = () => {
        const winner = game.checkWinner();
        if (winner) {
            showWinner(winner);
            return true;
        }
        return false;
    };

    const handleCpuRoulette = (cpu) => {
        const colors = ['red', 'yellow', 'green', 'blue'];
        const randomColor = colors[Math.floor(Math.random() * colors.length)];

        setGameMessage(`${cpu.name} chose ${randomColor} and is drawing...`);

        setTimeout(() => {
            const drawnCards = game.drawUntilColor(cpu, randomColor);
            game.rouletteActive = false;

            setGameMessage(`${cpu.name} drew ${drawnCards.length} card(s) until getting ${randomColor}!`);

            if (cpu.isEliminated) {
                showElimination(cpu.name);
            }

            updateGame();

            setTimeout(() => {
                game.nextTurn();
                updateGame();

                const nextPlayer = game.players[game.currentPlayerIndex];
                setIsAnimating(false);
                setGameMessage(`${nextPlayer.name}'s turn!`);

                if (!nextPlayer.isHuman && !nextPlayer.isEliminated) {
                    setNeedsCpuTurn(true);
                }
            }, 1500);
        }, 1000);
    };

    // PHASE 3: CPU chooses best target for 7 card swap based on difficulty
    const chooseCpuSwapTarget = (cpu) => {
        const otherPlayers = game.players.filter((p, idx) =>
            idx !== game.currentPlayerIndex && !p.isEliminated
        );

        if (otherPlayers.length === 0) return null;

        if (difficulty === 'easy') {
            // Easy: Random player
            return otherPlayers[Math.floor(Math.random() * otherPlayers.length)];
        } else if (difficulty === 'medium') {
            // Medium: Player with most cards
            otherPlayers.sort((a, b) => b.hand.length - a.hand.length);
            return otherPlayers[0];
        } else {
            // Hard: Strategic
            // If CPU has few cards (3 or less), swap with player who has most cards
            // If CPU has many cards, swap with player closest to winning (fewest cards)
            if (cpu.hand.length <= 3) {
                otherPlayers.sort((a, b) => b.hand.length - a.hand.length);
                return otherPlayers[0];
            } else {
                otherPlayers.sort((a, b) => a.hand.length - b.hand.length);
                return otherPlayers[0];
            }
        }
    };

    const cpuTurn = () => {
        if (!game || showWinnerCard || isEndingFromElimination) return;

        const cpu = game.players[game.currentPlayerIndex];

        if (cpu.isHuman || cpu.isEliminated) {
            setIsAnimating(false);
            const nextPlayer = game.players[game.currentPlayerIndex];
            setGameMessage(`${nextPlayer.name}'s turn!`);
            return;
        }

        if (game.rouletteActive) {
            handleCpuRoulette(cpu);
            return;
        }

        setIsAnimating(true);
        const validCards = cpu.hand.filter(card => game.canPlay(card));

        setGameMessage(`${cpu.name} is thinking...`);

        const thinkTime = difficulty === 'easy' ? 800 : difficulty === 'medium' ? 1200 : 1500;

        setTimeout(() => {
            if (game.isStacking && validCards.length > 0) {
                const stackableCards = validCards.filter(c => game.isStackableDrawCard(c));

                if (stackableCards.length > 0) {
                    const cardToPlay = stackableCards[0];
                    let chosenColor = null;

                    if (cardToPlay.card_type === 'wild') {
                        const colorCount = { red: 0, yellow: 0, green: 0, blue: 0 };
                        cpu.hand.forEach(card => {
                            if (card.color && colorCount[card.color] !== undefined) {
                                colorCount[card.color]++;
                            }
                        });
                        chosenColor = Object.keys(colorCount).reduce((a, b) =>
                            colorCount[a] > colorCount[b] ? a : b
                        );
                    }

                    game.playCard(cpu, cardToPlay, chosenColor);
                    setGameMessage(`${cpu.name} stacked ${cardToPlay.toString()}! Draw penalty now ${game.stackedDrawCount}!`);
                    updateGame();

                    setTimeout(() => {
                        setIsAnimating(false);
                        const nextPlayer = game.players[game.currentPlayerIndex];
                        setGameMessage(`${nextPlayer.name}'s turn!`);

                        if (!nextPlayer.isHuman && !nextPlayer.isEliminated) {
                            setNeedsCpuTurn(true);
                        }
                    }, 1000);
                    return;
                }

                game.executeStackedDraw();
                setGameMessage(`${cpu.name} drew ${game.stackedDrawCount} cards!`);

                if (cpu.isEliminated) {
                    showElimination(cpu.name);
                }

                updateGame();

                setTimeout(() => {
                    setIsAnimating(false);
                    const nextPlayer = game.players[game.currentPlayerIndex];
                    setGameMessage(`${nextPlayer.name}'s turn!`);

                    if (!nextPlayer.isHuman && !nextPlayer.isEliminated) {
                        setNeedsCpuTurn(true);
                    }
                }, 1500);
                return;
            }

            if (validCards.length > 0) {
                let cardToPlay;

                if (difficulty === 'easy') {
                    cardToPlay = validCards[Math.floor(Math.random() * validCards.length)];
                } else if (difficulty === 'medium') {
                    const actions = validCards.filter(c => c.card_type === 'action');
                    const numbers = validCards.filter(c => c.card_type === 'number');
                    const wilds = validCards.filter(c => c.card_type === 'wild');

                    if (actions.length > 0) {
                        cardToPlay = actions[Math.floor(Math.random() * actions.length)];
                    } else if (numbers.length > 0) {
                        cardToPlay = numbers[Math.floor(Math.random() * numbers.length)];
                    } else {
                        cardToPlay = wilds[0];
                    }
                } else {
                    const actions = validCards.filter(c => c.card_type === 'action');
                    const numbers = validCards.filter(c => c.card_type === 'number');
                    const wilds = validCards.filter(c => c.card_type === 'wild');

                    if (actions.length > 0) {
                        cardToPlay = actions[0];
                    } else if (numbers.length > 0) {
                        numbers.sort((a, b) => parseInt(b.value || 0) - parseInt(a.value || 0));
                        cardToPlay = numbers[0];
                    } else {
                        cardToPlay = wilds[0];
                    }
                }

                let chosenColor = null;
                if (cardToPlay.card_type === 'wild') {
                    const colorCount = { red: 0, yellow: 0, green: 0, blue: 0 };
                    cpu.hand.forEach(card => {
                        if (card.color && colorCount[card.color] !== undefined) {
                            colorCount[card.color]++;
                        }
                    });
                    chosenColor = Object.keys(colorCount).reduce((a, b) =>
                        colorCount[a] > colorCount[b] ? a : b
                    );
                }

                if (cardToPlay.value === '7') {
                    // PHASE 3: Use difficulty-based target selection
                    const targetPlayer = chooseCpuSwapTarget(cpu);

                    if (targetPlayer) {
                        const targetIndex = game.players.indexOf(targetPlayer);

                        game.playCard(cpu, cardToPlay);
                        game.swapHands(game.currentPlayerIndex, targetIndex);

                        // PHASE 3: Show swap message
                        setGameMessage(`${cpu.name} played 7 and swapped hands with ${targetPlayer.name}!`);
                        updateGame();

                        setTimeout(() => {
                            if (checkForWinner()) return;
                            game.nextTurn();
                            updateGame();

                            setTimeout(() => {
                                setIsAnimating(false);
                                const nextPlayer = game.players[game.currentPlayerIndex];
                                setGameMessage(`${nextPlayer.name}'s turn!`);

                                if (!nextPlayer.isHuman && !nextPlayer.isEliminated) {
                                    setNeedsCpuTurn(true);
                                }
                            }, 800);
                        }, 1500);
                        return;
                    }
                }

                if (cardToPlay.value === 'discard_all') {
                    game.playCard(cpu, cardToPlay);

                    const colorCount = { red: 0, yellow: 0, green: 0, blue: 0 };
                    cpu.hand.forEach(card => {
                        if (card.color && colorCount[card.color] !== undefined) {
                            colorCount[card.color]++;
                        }
                    });
                    const mostCommonColor = Object.keys(colorCount).reduce((a, b) =>
                        colorCount[a] > colorCount[b] ? a : b
                    );

                    const discardedCount = game.discardAllColor(cpu, mostCommonColor);

                    setGameMessage(`${cpu.name} discarded ${discardedCount} ${mostCommonColor} card(s)!`);
                    updateGame();

                    setTimeout(() => {
                        if (checkForWinner()) return;
                        game.nextTurn();
                        updateGame();

                        setTimeout(() => {
                            setIsAnimating(false);
                            const nextPlayer = game.players[game.currentPlayerIndex];
                            setGameMessage(`${nextPlayer.name}'s turn!`);

                            if (!nextPlayer.isHuman && !nextPlayer.isEliminated) {
                                setNeedsCpuTurn(true);
                            }
                        }, 800);
                    }, 1500);
                    return;
                }

                // PHASE 3: Check for 0 card before playing
                const isZeroCard = cardToPlay.value === '0';

                game.playCard(cpu, cardToPlay, chosenColor);
                setGameMessage(`${cpu.name} played ${cardToPlay.toString()}`);
                updateGame();

                // PHASE 3: Show hand rotation popup for 0 card
                if (isZeroCard) {
                    showHandRotation(game.direction);
                }

                setTimeout(() => {
                    if (checkForWinner()) return;
                    game.nextTurn();
                    updateGame();

                    if (game.rouletteActive) {
                        const nextPlayer = game.players[game.currentPlayerIndex];

                        if (nextPlayer.isHuman && !nextPlayer.isEliminated) {
                            setGameMessage(`${nextPlayer.name}, choose a color to draw until you get it!`);
                            setShowRouletteColorPicker(true);
                            setIsAnimating(false);
                        } else if (!nextPlayer.isEliminated) {
                            handleCpuRoulette(nextPlayer);
                        }
                    } else {
                        setTimeout(() => {
                            setIsAnimating(false);
                            const nextPlayer = game.players[game.currentPlayerIndex];
                            setGameMessage(`${nextPlayer.name}'s turn!`);

                            if (!nextPlayer.isHuman && !nextPlayer.isEliminated) {
                                setNeedsCpuTurn(true);
                            }
                        }, 800);
                    }
                }, 1000);
            } else {
                const drawnCards = game.drawUntilPlayable(cpu);
                setGameMessage(`${cpu.name} drew ${drawnCards.length} card(s)`);

                if (cpu.isEliminated) {
                    showElimination(cpu.name);
                }

                updateGame();

                setTimeout(() => {
                    game.nextTurn();
                    updateGame();

                    const nextPlayer = game.players[game.currentPlayerIndex];
                    setIsAnimating(false);
                    setGameMessage(`${nextPlayer.name}'s turn!`);

                    if (!nextPlayer.isHuman && !nextPlayer.isEliminated) {
                        setNeedsCpuTurn(true);
                    }
                }, 1500);
            }
        }, thinkTime);
    };

    useEffect(() => {
        if (game) {
            return;
        }

        const providedPlayerNames = Array.isArray(playerNamesFromSetup)
            ? playerNamesFromSetup.map((name) => String(name || '').trim()).filter(Boolean)
            : [];

        const normalizedHumans = Array.isArray(humanPlayersFromSetup)
            ? humanPlayersFromSetup.map((name) => String(name || '').trim().toLowerCase()).filter(Boolean)
            : [];

        const players = providedPlayerNames.length >= 2
            ? providedPlayerNames.map((name, index) => {
                const isHuman = normalizedHumans.length
                    ? normalizedHumans.includes(String(name || '').trim().toLowerCase())
                    : (allHumansFromSetup ? true : index === 0);
                return new Player(name, isHuman);
            })
            : [new Player(playerName, true)];

        if (providedPlayerNames.length < 2) {
            for (let i = 1; i < numPlayers; i++) {
                players.push(new Player(`CPU ${i}`, false));
            }
        }

        const newGame = new GameState(players);
        setGame(newGame);
        setLastHandSizes(players.map(p => p.hand.length));
        setLastRevealedByPlayer({});
        cpuTurnLockRef.current = '';

        const startingPlayer = newGame.players[newGame.currentPlayerIndex];
        setGameMessage(`${startingPlayer.name} starts!`);

        if (!startingPlayer.isHuman) {
            setTimeout(() => {
                setNeedsCpuTurn(true);
            }, 1500);
        }
    }, [playerName, numPlayers, playerNamesFromSetup, humanPlayersFromSetup, allHumansFromSetup, hasRouteState, game, initialSessionSnapshot?.serializedGame]);

    const applyRemoteSnapshotIfChanged = (snapshot, remoteUpdatedAt) => {
        if (!snapshot || !snapshot.serializedGame) return false;
        if (showColorPicker || showPlayerPicker || showDiscardAllPicker || showRouletteColorPicker || selectedCard) return false;
        const remoteUpdatedMs = Number.isFinite(Date.parse(remoteUpdatedAt || ''))
            ? Date.parse(remoteUpdatedAt || '')
            : 0;
        if (remoteUpdatedMs && remoteUpdatedMs <= lastAppliedRemoteUpdatedAtRef.current) {
            return false;
        }
        if (remoteUpdatedMs && remoteUpdatedMs < (lastOutboundWriteAtRef.current - 250)) {
            return false;
        }

        const normalizedSnapshot = {
            serializedGame: snapshot.serializedGame,
            rouletteDrawnCards: Array.isArray(snapshot.rouletteDrawnCards) ? snapshot.rouletteDrawnCards : [],
            gameMessage: typeof snapshot.gameMessage === 'string' ? snapshot.gameMessage : '',
            gameLog: Array.isArray(snapshot.gameLog) ? snapshot.gameLog : [],
            lastHandSizes: Array.isArray(snapshot.lastHandSizes) ? snapshot.lastHandSizes : [],
        };

        if (!isIncomingVirtualSnapshotDifferent(snapshotSignatureRef, normalizedSnapshot)) {
            return false;
        }

        const hydrated = hydrateUnoNmGameState(normalizedSnapshot.serializedGame);
        if (!hydrated) return false;

        markRemoteVirtualSnapshotApplied(snapshotSignatureRef, suppressNextSessionWriteRef, normalizedSnapshot);
        setGame(hydrated);
        setRouletteDrawnCards(normalizedSnapshot.rouletteDrawnCards);
        setGameMessage(normalizedSnapshot.gameMessage);
        setGameLog(normalizedSnapshot.gameLog);
        setLastHandSizes(normalizedSnapshot.lastHandSizes);
        setNeedsCpuTurn(false);
        setIsAnimating(false);
        if (remoteUpdatedMs) {
            lastAppliedRemoteUpdatedAtRef.current = remoteUpdatedMs;
        }
        return true;
    };

    useEffect(() => {
        if (!isSessionMode) return;
        const snapshot = sessionGame?.snapshot || location.state?.__sessionGameSnapshot;
        applyRemoteSnapshotIfChanged(snapshot, sessionGame?.updatedAt);
    }, [
        isSessionMode,
        location.state,
        selectedCard,
        sessionGame?.snapshot,
        sessionGame?.updatedAt,
        showColorPicker,
        showDiscardAllPicker,
        showPlayerPicker,
        showRouletteColorPicker,
    ]);

    useEffect(() => {
        if (!isSessionMode || !session) return;
        if (sessionGame) return;
        clearSessionGameStateCaches(sessionCode);
        navigate('/uno-nm-setup', { replace: true });
    }, [isSessionMode, navigate, session, sessionCode, sessionGame]);

    useEffect(() => {
        if (wasSessionModeRef.current && !isSessionMode) {
            clearSessionGameStateCaches(sessionCode);
            navigate('/uno-nm-setup', { replace: true });
        }
        wasSessionModeRef.current = isSessionMode;
    }, [isSessionMode, navigate, sessionCode]);

    useEffect(() => {
        if (!isSessionMode || !sessionCode) return undefined;
        let cancelled = false;

        const pull = async () => {
            try {
                const data = await sessionApi.getVirtualGameState(sessionCode);
                const state = data?.state;
                if (cancelled || !state || state.game !== 'uno-nm' || !state.snapshot?.serializedGame) return;
                applyRemoteSnapshotIfChanged(state.snapshot, state.updatedAt);
            } catch {
                // no-op
            }
        };

        pull().catch(() => null);
        const interval = setInterval(() => {
            pull().catch(() => null);
        }, 1000);

        return () => {
            cancelled = true;
            clearInterval(interval);
        };
    }, [
        isSessionMode,
        selectedCard,
        sessionCode,
        showColorPicker,
        showDiscardAllPicker,
        showPlayerPicker,
        showRouletteColorPicker,
    ]);

    useEffect(() => {
        if (!isSessionMode || !sessionCode || !virtualGameSyncTick) return;
        sessionApi.getVirtualGameState(sessionCode).then((data) => {
            const state = data?.state;
            if (!state || state.game !== 'uno-nm' || !state.snapshot?.serializedGame) return;
            applyRemoteSnapshotIfChanged(state.snapshot, state.updatedAt);
        }).catch(() => null);
    }, [
        isSessionMode,
        selectedCard,
        sessionCode,
        showColorPicker,
        showDiscardAllPicker,
        showPlayerPicker,
        showRouletteColorPicker,
        virtualGameSyncTick,
    ]);

    useEffect(() => {
        const canRunCpuTurns = !isSessionMode || isSessionHost;
        if (canRunCpuTurns && needsCpuTurn && game && !isAnimating && !isEndingFromElimination) {
            const turnKey = getCpuTurnKey();
            if (cpuTurnLockRef.current === turnKey) {
                setNeedsCpuTurn(false);
                return;
            }
            cpuTurnLockRef.current = turnKey;
            setNeedsCpuTurn(false);
            cpuTurn();
        }
    }, [needsCpuTurn, game, isAnimating, isEndingFromElimination, isSessionHost, isSessionMode]);

    useEffect(() => {
        if (!game) return;

        game.players.forEach((player, idx) => {
            if (player.hand.length === 1 && lastHandSizes[idx] > 1 && !player.isEliminated) {
                setUnoPlayer(player.name);
                setShowUnoMessage(true);
                setTimeout(() => {
                    setShowUnoMessage(false);
                    setUnoPlayer(null);
                }, 2000);
            }
        });

        setLastHandSizes(game.players.map(p => p.hand.length));
    }, [game?.players.map(p => p.hand.length).join(',')]);

    useEffect(() => {
        if (!gameMessage || gameMessage === lastLoggedMessage.current) {
            return;
        }

        const findActor = () => {
            if (!game) {
                return null;
            }

            const patterns = [
                /^(.+?) starts!$/,
                /^(.+?)'s turn!$/,
                /^(.+?) played /,
                /^(.+?) stacked /,
                /^(.+?) drew /,
                /^(.+?) is thinking/,
                /^(.+?) chose /,
                /^(.+?), choose a color/
            ];

            for (const pattern of patterns) {
                const match = gameMessage.match(pattern);
                if (match && game.players.some((player) => player.name === match[1])) {
                    return match[1];
                }
            }

            if (gameMessage.startsWith('You ')) {
                const human = game.players.find((player) => player.isHuman);
                return human?.name || null;
            }

            return null;
        };

        const actor = findActor();
        setGameLog((prev) => {
            const lastEntry = prev[prev.length - 1];
            const sameAsLast = Boolean(lastEntry)
                && String(lastEntry.text || '') === String(gameMessage || '')
                && String(lastEntry.actor || '') === String(actor || '');
            if (sameAsLast) {
                return prev;
            }
            return [
                ...prev.slice(-49),
                {
                    id: `${Date.now()}-${prev.length}`,
                    actor,
                    text: gameMessage
                }
            ];
        });
        if (actor && /(?: played | stacked | discarded )/.test(gameMessage)) {
            const topCard = game?.discardPile?.[game.discardPile.length - 1];
            if (topCard) {
                setLastRevealedByPlayer((prev) => ({
                    ...prev,
                    [actor]: {
                        color: topCard.color ?? null,
                        value: topCard.value ?? null,
                        card_type: topCard.card_type ?? null
                    }
                }));
            }
        }
        lastLoggedMessage.current = gameMessage;
    }, [gameMessage, game]);

    const gameStateSignature = game
        ? [
            game.currentPlayerIndex,
            game.direction,
            game.currentColor,
            game.stackedDrawCount || 0,
            game.isStacking ? 1 : 0,
            game.rouletteActive ? 1 : 0,
            game.deck?.cards?.length || 0,
            game.discardPile?.length || 0,
            game.players.map((player) => `${player.name}:${player.hand.length}:${player.isHuman ? 1 : 0}:${player.isEliminated ? 1 : 0}`).join('|')
        ].join('::')
        : '';

    const localSessionSnapshot = useMemo(() => (
        game
            ? {
                serializedGame: serializeUnoNmGameState(game),
                rouletteDrawnCards,
                gameMessage,
                gameLog,
                lastHandSizes,
            }
            : null
    ), [game, gameStateSignature, rouletteDrawnCards, gameMessage, gameLog, lastHandSizes]);

    useEffect(() => {
        if (!localSessionSnapshot) return;
        markVirtualSnapshotSignature(snapshotSignatureRef, localSessionSnapshot);
    }, [localSessionSnapshot, gameStateSignature]);

    useEffect(() => {
        if (!game) return;

        storageManager.saveTemporaryData(
            UNO_NM_GAME_CACHE_KEY,
            stampSessionScopedGameState({
                setup: {
                    playerName,
                    numPlayers,
                    difficulty,
                    showCardCount,
                    playerNames: playerNamesFromSetup,
                    humanPlayers: humanPlayersFromSetup,
                    allHumans: allHumansFromSetup
                },
                serializedGame: serializeUnoNmGameState(game),
                selectedCard: selectedCard ? {
                    color: selectedCard.color ?? null,
                    value: selectedCard.value ?? null,
                    card_type: selectedCard.card_type ?? null
                } : null,
                showColorPicker,
                showPlayerPicker,
                showDiscardAllPicker,
                showRouletteColorPicker,
                rouletteDrawnCards,
                gameMessage,
                gameLog,
                lastHandSizes,
                showUnplayableCards,
                lastRevealedByPlayer
            }, isSessionMode, sessionCode),
            8
        );
    }, [
        game,
        gameStateSignature,
        playerName,
        numPlayers,
        difficulty,
        showCardCount,
        playerNamesFromSetup,
        humanPlayersFromSetup,
        allHumansFromSetup,
        selectedCard,
        showColorPicker,
        showPlayerPicker,
        showDiscardAllPicker,
        showRouletteColorPicker,
        rouletteDrawnCards,
        gameMessage,
        gameLog,
        lastHandSizes,
        showUnplayableCards,
        lastRevealedByPlayer,
        isSessionMode,
        sessionCode
    ]);

    useEffect(() => {
        if (!isSessionMode || !sessionCode || !sessionAlias || !game || !localSessionSnapshot) return undefined;
        if (consumeSuppressedSessionWrite(suppressNextSessionWriteRef)) {
            return undefined;
        }

        const timer = setTimeout(() => {
            lastOutboundWriteAtRef.current = Date.now();
            sessionApi.updateVirtualGameState(sessionCode, {
                updatedByAlias: sessionAlias,
                game: 'uno-nm',
                snapshot: localSessionSnapshot,
            }).then(() => (
                emitRealtimeEvent(
                    'session-updated',
                    { code: sessionCode, action: 'virtual-game-state', game: 'uno-nm' },
                    sessionCode
                ).catch(() => null)
            )).catch(() => null);
        }, 180);
        return () => clearTimeout(timer);
    }, [
        game,
        gameStateSignature,
        isSessionMode,
        localSessionSnapshot,
        emitRealtimeEvent,
        sessionAlias,
        sessionCode,
    ]);

    useEffect(() => {
        if (!game || !isSessionMode || !isSessionHost || isAnimating || showWinnerCard || isEndingFromElimination) return;
        const actingPlayer = game.players[game.currentPlayerIndex];
        if (actingPlayer && !actingPlayer.isHuman && !actingPlayer.isEliminated) {
            setNeedsCpuTurn(true);
        } else {
            cpuTurnLockRef.current = '';
        }
    }, [game, gameStateSignature, isAnimating, isEndingFromElimination, isSessionHost, isSessionMode, showWinnerCard]);

    useEffect(() => {
        if (!isSessionMode || !game) return;
        const aliases = Array.isArray(session?.participants)
            ? session.participants
                .map((participant) => String(participant?.alias || '').trim().toLowerCase())
                .filter(Boolean)
            : [];
        if (!aliases.length) return;

        const humanAliasSet = new Set(aliases);
        let changed = false;
        game.players.forEach((player) => {
            const shouldBeHuman = humanAliasSet.has(String(player?.name || '').trim().toLowerCase());
            if (player.isHuman !== shouldBeHuman) {
                player.isHuman = shouldBeHuman;
                changed = true;
            }
        });

        if (changed) {
            updateGame();
        }
    }, [game, isSessionMode, session?.participants]);

    if (!game) {
        return (
            <div style={{
                minHeight: '100vh',
                backgroundColor: theme.bgMedium,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: theme.textPrimary
            }}>
                Loading...
            </div>
        );
    }

    const currentPlayer = game.players[game.currentPlayerIndex];
    const allHumanMode = game.players.every((player) => player.isHuman);
    const localHumanPlayer = game.players.find((player) => player.isHuman) || currentPlayer;
    const viewerPlayer = isSessionMode
        ? (game.players.find((player) => player.name.toLowerCase() === sessionAlias.toLowerCase()) || localHumanPlayer)
        : localHumanPlayer;
    const activeHandPlayer = isSessionMode
        ? viewerPlayer
        : (allHumanMode ? currentPlayer : localHumanPlayer);
    const isPlayerTurn = !currentPlayer.isEliminated && (
        isSessionMode
            ? currentPlayer.name.toLowerCase() === viewerPlayer.name.toLowerCase()
            : (allHumanMode || currentPlayer === localHumanPlayer)
    );
    const opponentPlayers = isSessionMode
        ? game.players.filter((player) => player.name !== viewerPlayer.name)
        : (allHumanMode
            ? game.players.filter((_, index) => index !== game.currentPlayerIndex)
            : game.players.filter((player) => !player.isHuman));
    const isMobile = window.innerWidth <= 768;

    const playableCards = [];
    const unplayableCards = [];

    if (activeHandPlayer && !activeHandPlayer.isEliminated) {
        activeHandPlayer.hand.forEach((card, idx) => {
            if (game.canPlay(card)) {
                playableCards.push({ card, originalIndex: idx });
            } else {
                unplayableCards.push({ card, originalIndex: idx });
            }
        });
    }

    const handleCardClick = (card) => {
        if (!isPlayerTurn || isAnimating || showWinnerCard || isEndingFromElimination) return;

        if (!game.canPlay(card)) {
            setGameMessage("❌ Can't play that card!");
            setTimeout(() => {
                setGameMessage(`${currentPlayer.name}'s turn!`);
            }, 1500);
            return;
        }

        if (card.value === '7') {
            setSelectedCard(card);
            setShowPlayerPicker(true);
        } else if (card.value === 'discard_all') {
            setSelectedCard(card);
            setShowDiscardAllPicker(true);
        } else if (card.card_type === 'wild' && card.value !== 'wild_color_roulette') {
            setSelectedCard(card);
            setShowColorPicker(true);
        } else {
            playCard(card);
        }
    };

    const playCard = (card, chosenColor = null) => {
        setIsAnimating(true);

        // PHASE 3: Check for 0 card before playing
        const isZeroCard = card.value === '0';

        setTimeout(() => {
            const success = game.playCard(currentPlayer, card, chosenColor);

            if (success) {
                setGameMessage(`${currentPlayer.name} played ${card.toString()}`);
                setShowColorPicker(false);
                setSelectedCard(null);
                updateGame();

                // PHASE 3: Show hand rotation popup for 0 card
                if (isZeroCard) {
                    showHandRotation(game.direction);
                }

                if (checkForWinner()) return;

                game.nextTurn();
                updateGame();

                if (game.rouletteActive) {
                    const nextPlayer = game.players[game.currentPlayerIndex];

                    if (nextPlayer.isHuman && !nextPlayer.isEliminated) {
                        setGameMessage(`${nextPlayer.name}, choose a color to draw until you get it!`);
                        setShowRouletteColorPicker(true);
                        setIsAnimating(false);
                    } else if (!nextPlayer.isEliminated) {
                        handleCpuRoulette(nextPlayer);
                    } else {
                        game.rouletteActive = false;
                        setIsAnimating(false);
                    }
                } else {
                    setTimeout(() => {
                        setIsAnimating(false);
                        const nextPlayer = game.players[game.currentPlayerIndex];
                        setGameMessage(`${nextPlayer.name}'s turn!`);

                        if (!nextPlayer.isHuman && !nextPlayer.isEliminated) {
                            setNeedsCpuTurn(true);
                        }
                    }, 600);
                }
            } else {
                setIsAnimating(false);
            }
        }, 300);
    };

    const handleDrawCard = () => {
        if (!isPlayerTurn || isAnimating || showWinnerCard || isEndingFromElimination) return;

        setIsAnimating(true);

        if (game.isStacking) {
            game.executeStackedDraw();
            setGameMessage(`${currentPlayer.name} drew ${game.stackedDrawCount} cards!`);

            if (activeHandPlayer.isEliminated) {
                showElimination(activeHandPlayer.name);
                if (!allHumanMode) {
                    endForHumanElimination(activeHandPlayer.name);
                    updateGame();
                    return;
                }
            }

            updateGame();

            setTimeout(() => {
                setIsAnimating(false);
                const nextPlayer = game.players[game.currentPlayerIndex];
                setGameMessage(`${nextPlayer.name}'s turn!`);

                if (!nextPlayer.isHuman && !nextPlayer.isEliminated) {
                    setNeedsCpuTurn(true);
                }
            }, 1500);
            return;
        }

        const drawnCards = game.drawUntilPlayable(currentPlayer);
        setGameMessage(`${currentPlayer.name} drew ${drawnCards.length} card(s)`);

        if (activeHandPlayer.isEliminated) {
            showElimination(activeHandPlayer.name);
            if (!allHumanMode) {
                endForHumanElimination(activeHandPlayer.name);
                updateGame();
                return;
            }
        }

        updateGame();

        setTimeout(() => {
            game.nextTurn();
            updateGame();

            const nextPlayer = game.players[game.currentPlayerIndex];
            setGameMessage(`${nextPlayer.name}'s turn!`);
            setIsAnimating(false);

            if (!nextPlayer.isHuman && !nextPlayer.isEliminated) {
                setNeedsCpuTurn(true);
            }
        }, 1000);
    };

    const handleColorChoice = (color) => {
        if (selectedCard) {
            playCard(selectedCard, color);
        }
    };

    const handleRouletteColorChoice = (color) => {
        setShowRouletteColorPicker(false);
        setGameMessage(`Drawing until you get ${color}...`);

        setTimeout(() => {
            const drawnCards = game.drawUntilColor(activeHandPlayer, color);
            game.rouletteActive = false;

            setRouletteDrawnCards(drawnCards);
            setGameMessage(`${currentPlayer.name} drew ${drawnCards.length} card(s) until getting ${color}!`);

            if (activeHandPlayer.isEliminated) {
                showElimination(activeHandPlayer.name);
                if (!allHumanMode) {
                    endForHumanElimination(activeHandPlayer.name);
                    updateGame();
                    return;
                }
            }

            updateGame();

            setTimeout(() => {
                setRouletteDrawnCards([]);
                game.nextTurn();
                updateGame();

                const nextPlayer = game.players[game.currentPlayerIndex];
                setIsAnimating(false);
                setGameMessage(`${nextPlayer.name}'s turn!`);

                if (!nextPlayer.isHuman && !nextPlayer.isEliminated) {
                    setNeedsCpuTurn(true);
                }
            }, 2000);
        }, 500);
    };

    const handlePlayerChoice = (targetIndex) => {
        if (selectedCard) {
            const targetPlayer = game.players[targetIndex];

            game.playCard(currentPlayer, selectedCard);
            game.swapHands(game.currentPlayerIndex, targetIndex);

            // PHASE 3: Show swap message
            setGameMessage(`${currentPlayer.name} swapped hands with ${targetPlayer.name}!`);
            setShowPlayerPicker(false);
            setSelectedCard(null);
            updateGame();

            if (checkForWinner()) return;

            setTimeout(() => {
                game.nextTurn();
                updateGame();

                setTimeout(() => {
                    setIsAnimating(false);
                    const nextPlayer = game.players[game.currentPlayerIndex];
                    setGameMessage(`${nextPlayer.name}'s turn!`);

                    if (!nextPlayer.isHuman && !nextPlayer.isEliminated) {
                        setNeedsCpuTurn(true);
                    }
                }, 600);
            }, 1500);
        }
    };

    const handleDiscardAllChoice = (color) => {
        if (selectedCard) {
            game.playCard(currentPlayer, selectedCard);

            const discardedCount = game.discardAllColor(activeHandPlayer, color);

            setGameMessage(`${currentPlayer.name} discarded ${discardedCount} ${color} card(s)!`);
            setShowDiscardAllPicker(false);
            setSelectedCard(null);
            updateGame();

            if (checkForWinner()) return;

            setTimeout(() => {
                game.nextTurn();
                updateGame();

                setTimeout(() => {
                    setIsAnimating(false);
                    const nextPlayer = game.players[game.currentPlayerIndex];
                    setGameMessage(`${nextPlayer.name}'s turn!`);

                    if (!nextPlayer.isHuman && !nextPlayer.isEliminated) {
                        setNeedsCpuTurn(true);
                    }
                }, 600);
            }, 1500);
        }
    };

    const handleRestart = async () => {
        const restartLocked = isSessionMode && !isSessionHost;
        if (restartLocked) {
            return;
        }
        if (window.confirm('Start a new game? Current progress will be lost.')) {
            if (isSessionMode && isSessionHost && sessionCode) {
                try {
                    await sessionApi.endVirtualGame(sessionCode, {
                        endedByAlias: sessionHostAlias || sessionAlias || undefined
                    });
                    emitRealtimeEvent(
                        'session-updated',
                        { code: sessionCode, action: 'virtual-game-ended', game: 'uno-nm' },
                        sessionCode
                    ).catch(() => null);
                } catch {
                    // no-op
                }
            }
            storageManager.removeTemporaryData(UNO_NM_GAME_CACHE_KEY);
            navigate('/uno-nm-setup');
        }
    };

    const handleSaveGameState = () => {
        if (!canOpenExitMenu) {
            return;
        }
        if (!game) return;
        const saveId = storageManager.saveGame(
            'uno-nm',
            {
                setup: {
                    playerName,
                    numPlayers,
                    difficulty,
                    showCardCount,
                    playerNames: playerNamesFromSetup,
                    humanPlayers: humanPlayersFromSetup,
                    allHumans: allHumansFromSetup
                },
                serializedGame: serializeUnoNmGameState(game),
                rouletteDrawnCards,
                gameMessage,
                gameLog,
                lastHandSizes,
                savedAt: Date.now(),
                session: isSessionMode ? {
                    code: sessionCode,
                    alias: sessionAlias,
                    isHost: isSessionHost
                } : null
            },
            `UNO NM ${isSessionMode ? `Session ${sessionCode}` : 'Local'} ${new Date().toLocaleString()}`
        );
        if (saveId) {
            setGameMessage('Game state saved.');
            setShowExitMenu(false);
        } else {
            setGameMessage('Failed to save game state.');
        }
    };

    const handleEndCurrentGame = async () => {
        const endLocked = isSessionMode && !isSessionHost;
        if (endLocked) {
            setGameMessage('Only the host can end this session game.');
            return;
        }
        if (isSessionMode && isSessionHost && sessionCode) {
            try {
                await sessionApi.endVirtualGame(sessionCode, {
                    endedByAlias: sessionHostAlias || sessionAlias || undefined
                });
                emitRealtimeEvent(
                    'session-updated',
                    { code: sessionCode, action: 'virtual-game-ended', game: 'uno-nm' },
                    sessionCode
                ).catch(() => null);
                emitRealtimeEvent(
                    'messages-updated',
                    { code: sessionCode, action: 'virtual-game-ended', game: 'uno-nm' },
                    sessionCode
                ).catch(() => null);
            } catch {
                setGameMessage('Failed to end current game.');
                return;
            }
        }
        storageManager.removeTemporaryData(UNO_NM_GAME_CACHE_KEY);
        setShowExitMenu(false);
        navigate('/uno-nm-setup');
    };

    const topCard = game.getTopCard();

    return (
        <div style={{
            padding: isMobile ? '10px' : '20px',
            minHeight: '100vh',
            backgroundColor: theme.bgMedium,
            color: theme.textPrimary,
            display: 'flex',
            flexDirection: 'column',
            position: 'relative'
        }}>
            <BackButton to="/uno-nm-setup" />
            <SettingsButton />
            <HelpButton helpText="UNO NO MERCY! Stack draw cards, eliminate opponents, show no mercy!" />
            <SocialButton />
            {isSessionMode && (
                <div style={{
                    marginTop: '56px',
                    textAlign: 'center',
                    color: theme.textSecondary,
                    fontSize: isMobile ? '0.8rem' : '0.9rem'
                }}>
                    Session mode: you can only play your own turn and only see your own cards.
                </div>
            )}

            <div style={{
                position: 'fixed',
                top: '20px',
                left: '50%',
                transform: 'translateX(-50%)',
                display: 'flex',
                gap: '8px',
                zIndex: 100,
            }}>
                <button
                    onClick={handleRestart}
                    disabled={isSessionMode && !isSessionHost}
                    style={{
                        padding: isMobile ? '8px 14px' : '10px 18px',
                        fontSize: isMobile ? '0.85rem' : '0.95rem',
                        backgroundColor: theme.danger,
                        color: theme.textPrimary,
                        border: 'none',
                        borderRadius: '5px',
                        cursor: (isSessionMode && !isSessionHost) ? 'not-allowed' : 'pointer',
                        fontFamily: 'inherit',
                        opacity: (isSessionMode && !isSessionHost) ? 0.6 : 1
                    }}
                >
                    Restart
                </button>
                {canOpenExitMenu && (
                    <button
                        onClick={() => setShowExitMenu(true)}
                        style={{
                            padding: isMobile ? '8px 14px' : '10px 18px',
                            fontSize: isMobile ? '0.85rem' : '0.95rem',
                            backgroundColor: theme.secondary,
                            color: theme.textPrimary,
                            border: `1px solid ${theme.light}`,
                            borderRadius: '5px',
                            cursor: 'pointer',
                            fontFamily: 'inherit'
                        }}
                    >
                        Exit Game
                    </button>
                )}
            </div>

            {canOpenExitMenu && showExitMenu && (
                <div
                    onClick={() => setShowExitMenu(false)}
                    style={{
                        position: 'fixed',
                        inset: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.72)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 1200,
                        padding: '20px'
                    }}
                >
                    <div
                        onClick={(event) => event.stopPropagation()}
                        style={{
                            width: '100%',
                            maxWidth: '420px',
                            backgroundColor: theme.bgMedium,
                            border: `2px solid ${theme.light}`,
                            borderRadius: '12px',
                            padding: '16px',
                            display: 'grid',
                            gap: '10px'
                        }}
                    >
                        <h3 style={{ margin: 0 }}>Exit Game</h3>
                        <div style={{ color: theme.textSecondary, fontSize: '0.9rem' }}>
                            Choose what to do with the current match.
                        </div>
                        <button
                            onClick={handleSaveGameState}
                            style={{
                                padding: '10px',
                                border: 'none',
                                borderRadius: '8px',
                                backgroundColor: theme.success,
                                color: theme.textPrimary,
                                fontFamily: 'inherit',
                                cursor: 'pointer',
                                fontWeight: 'bold'
                            }}
                        >
                            Save Game State
                        </button>
                        <button
                            onClick={handleEndCurrentGame}
                            disabled={isSessionMode && !isSessionHost}
                            style={{
                                padding: '10px',
                                border: 'none',
                                borderRadius: '8px',
                                backgroundColor: theme.danger,
                                color: theme.textPrimary,
                                fontFamily: 'inherit',
                                cursor: (isSessionMode && !isSessionHost) ? 'not-allowed' : 'pointer',
                                fontWeight: 'bold',
                                opacity: (isSessionMode && !isSessionHost) ? 0.6 : 1
                            }}
                        >
                            End Current Game
                        </button>
                        <button
                            onClick={() => setShowExitMenu(false)}
                            style={{
                                padding: '10px',
                                borderRadius: '8px',
                                border: `1px solid ${theme.secondary}`,
                                backgroundColor: 'transparent',
                                color: theme.textPrimary,
                                fontFamily: 'inherit',
                                cursor: 'pointer',
                                fontWeight: 'bold'
                            }}
                        >
                            Cancel (Return to Game)
                        </button>
                    </div>
                </div>
            )}

            <div style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                marginTop: '60px',
                minHeight: 0
            }}>
                <div style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'flex-start',
                    gap: isMobile ? '8px' : '14px',
                    flexWrap: 'wrap',
                    padding: isMobile ? '5px' : '10px'
                }}>
                    {opponentPlayers.map((cpu, idx) => (
                        <div key={idx} style={{
                            textAlign: 'center',
                            width: isMobile ? '98px' : '124px',
                            minWidth: 0,
                            opacity: cpu.isEliminated ? 0.3 : 1,
                            display: 'grid',
                            gap: '4px',
                            justifyItems: 'center'
                        }}>
                            <h4 style={{
                                marginBottom: '5px',
                                color: cpu.isEliminated ? theme.textDisabled :
                                    (game.players[game.currentPlayerIndex] === cpu ? theme.danger : theme.textSecondary),
                                fontSize: isMobile ? '0.7rem' : '1rem',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis'
                            }}>
                                {cpu.name} {cpu.isEliminated ? '(OUT)' : (showCardCount && `- ${cpu.hand.length}`)}
                            </h4>
                            <div style={{ position: 'relative', width: '44px', height: '62px' }}>
                                {Array.from({ length: Math.max(1, Math.min(3, cpu.hand.length)) }).map((_, stackIndex) => (
                                    <div
                                        key={`${cpu.name}-stack-${stackIndex}`}
                                        style={{
                                            position: 'absolute',
                                            left: `${stackIndex * 4}px`,
                                            top: `${stackIndex * 3}px`,
                                            transform: `scale(${isMobile ? 0.5 : 0.58})`,
                                            transformOrigin: 'top left'
                                        }}
                                    >
                                        <UnoCard faceDown={true} size="small" />
                                    </div>
                                ))}
                            </div>
                            <div style={{ minHeight: '52px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                {lastRevealedByPlayer[cpu.name] ? (
                                    <div style={{ transform: `scale(${isMobile ? 0.5 : 0.58})`, transformOrigin: 'center top' }}>
                                        <UnoCard card={lastRevealedByPlayer[cpu.name]} size="small" />
                                    </div>
                                ) : (
                                    <span style={{ fontSize: '0.7rem', color: theme.textDisabled }}>No reveal</span>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            <div style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: 'column',
                gap: isMobile ? '15px' : '30px',
                minHeight: 0
            }}>
                <div style={{
                    display: 'flex',
                    gap: isMobile ? '20px' : '50px',
                    alignItems: 'center',
                    flexWrap: 'nowrap'
                }}>
                    <div style={{ textAlign: 'center' }}>
                        <button
                            onClick={handleDrawCard}
                            disabled={!isPlayerTurn || isAnimating || activeHandPlayer.isEliminated || isEndingFromElimination}
                            style={{
                                background: 'none',
                                border: 'none',
                                padding: 0,
                                cursor: (isPlayerTurn && !isAnimating && !activeHandPlayer.isEliminated && !isEndingFromElimination) ? 'pointer' : 'not-allowed',
                                transition: 'transform 0.2s ease, filter 0.2s ease',
                                filter: (isPlayerTurn && !isAnimating && !activeHandPlayer.isEliminated && !isEndingFromElimination) ? 'brightness(1)' : 'brightness(0.6)',
                            }}
                            onMouseEnter={(e) => {
                                if (isPlayerTurn && !isAnimating && !activeHandPlayer.isEliminated && !isEndingFromElimination) {
                                    e.currentTarget.style.transform = 'translateY(-8px)';
                                }
                            }}
                            onMouseLeave={(e) => {
                                if (isPlayerTurn && !isAnimating && !activeHandPlayer.isEliminated && !isEndingFromElimination) {
                                    e.currentTarget.style.transform = 'translateY(0)';
                                }
                            }}
                        >
                            <UnoCard
                                faceDown={true}
                                isPlayable={false}
                                size={isMobile ? "normal" : "large"}
                            />
                        </button>
                        <p style={{
                            marginTop: '5px',
                            fontSize: isMobile ? '0.8rem' : '1rem',
                            color: theme.textSecondary,
                            fontWeight: 'bold'
                        }}>
                            {game.isStacking ? `DRAW ${game.stackedDrawCount}!` : 'DRAW'}
                        </p>
                        {!isMobile && (
                            <p style={{
                                fontSize: '0.8rem',
                                color: theme.textDisabled,
                                marginTop: '5px'
                            }}>
                                {game.isStacking ? '(or stack!)' : '(until playable)'}
                            </p>
                        )}
                    </div>

                    <div style={{ textAlign: 'center' }}>
                        <UnoCard
                            card={topCard}
                            isPlayable={false}
                            size={isMobile ? "normal" : "large"}
                        />
                        <p style={{
                            marginTop: '5px',
                            fontSize: isMobile ? '0.9rem' : '1.2rem',
                            fontWeight: 'bold',
                            color: theme.textPrimary,
                            textTransform: 'uppercase'
                        }}>
                            <span style={{
                                color: {
                                    red: '#E74C3C',
                                    yellow: '#F39C12',
                                    green: '#27AE60',
                                    blue: '#3498DB'
                                }[game.currentColor]
                            }}>{game.currentColor}</span>
                        </p>
                    </div>
                </div>

                <div style={{
                    backgroundColor: theme.bgDark,
                    padding: isMobile ? '10px 20px' : '15px 30px',
                    borderRadius: '10px',
                    border: `2px solid ${game.isStacking ? theme.danger : theme.accent}`,
                    minWidth: isMobile ? '200px' : '300px',
                    textAlign: 'center'
                }}>
                    <p style={{ fontSize: isMobile ? '1rem' : '1.2rem', margin: 0 }}>{gameMessage}</p>
                </div>

                <GameLog
                    title="UNO:NM Play Log"
                    entries={gameLog}
                    maxHeight={isMobile ? 92 : 128}
                />
            </div>

            <div style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                minHeight: 0,
                marginBottom: isMobile ? '10px' : '20px'
            }}>
                <h3 style={{
                    marginBottom: '10px',
                    textAlign: 'center',
                    color: activeHandPlayer.isEliminated ? theme.textDisabled :
                        (isPlayerTurn ? theme.danger : theme.textSecondary),
                    fontSize: isMobile ? '0.9rem' : '1.2rem'
                }}>
                    {activeHandPlayer.name}'s Hand ({activeHandPlayer.hand.length}) {activeHandPlayer.isEliminated && '(ELIMINATED)'}
                </h3>

                {!activeHandPlayer.isEliminated && (
                    <>
                        {playableCards.length > 0 && (
                            <div style={{
                                overflowX: 'auto',
                                padding: isMobile ? '5px' : '10px',
                                marginBottom: unplayableCards.length > 0 ? '10px' : '0',
                                WebkitOverflowScrolling: 'touch'
                            }}>
                                <div style={{
                                    display: 'flex',
                                    gap: isMobile ? '5px' : '10px',
                                    justifyContent: 'center',
                                    minWidth: '100%',
                                    width: 'max-content',
                                    margin: '0 auto'
                                }}>
                                    {playableCards.map(({ card, originalIndex }) => (
                                        <motion.div
                                            key={originalIndex}
                                            initial={{ scale: 0.8, opacity: 0 }}
                                            animate={{ scale: 1, opacity: 1 }}
                                            transition={{ delay: originalIndex * 0.05 }}
                                            style={{
                                                flex: '0 0 auto'
                                            }}
                                        >
                                            <UnoCard
                                                card={card}
                                                isPlayable={isPlayerTurn && !isAnimating}
                                                onClick={() => handleCardClick(card)}
                                                size={isMobile ? "small" : "normal"}
                                            />
                                        </motion.div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {unplayableCards.length > 0 && (
                            <div style={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                padding: isMobile ? '5px' : '10px',
                                opacity: 0.5
                            }}>
                                {unplayableCards.length === 1 ? (
                                    <UnoCard
                                        card={unplayableCards[0].card}
                                        isPlayable={false}
                                        size={isMobile ? "small" : "normal"}
                                    />
                                ) : (
                                    <div
                                        onClick={() => setShowUnplayableCards(true)}
                                        style={{
                                            position: 'relative',
                                            width: isMobile ? '60px' : '80px',
                                            height: isMobile ? '90px' : '120px',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        {unplayableCards.slice(0, 3).map((item, idx) => (
                                            <div
                                                key={item.originalIndex}
                                                style={{
                                                    position: 'absolute',
                                                    top: `${idx * 3}px`,
                                                    left: `${idx * 3}px`,
                                                    zIndex: idx
                                                }}
                                            >
                                                <UnoCard
                                                    card={item.card}
                                                    isPlayable={false}
                                                    size={isMobile ? "small" : "normal"}
                                                />
                                            </div>
                                        ))}
                                        <div style={{
                                            position: 'absolute',
                                            bottom: '-20px',
                                            left: '50%',
                                            transform: 'translateX(-50%)',
                                            fontSize: '0.8rem',
                                            color: theme.textSecondary,
                                            whiteSpace: 'nowrap'
                                        }}>
                                            Tap to view ({unplayableCards.length})
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {playableCards.length === 0 && unplayableCards.length > 0 && (
                            <p style={{
                                textAlign: 'center',
                                color: theme.textSecondary,
                                fontSize: isMobile ? '0.8rem' : '0.9rem',
                                marginTop: '5px'
                            }}>
                                {game.isStacking ? 'Stack or draw!' : 'No playable cards - draw!'}
                            </p>
                        )}
                    </>
                )}
            </div>

            <AnimatePresence>
                {showUnoMessage && (
                    <motion.div
                        initial={{ scale: 0, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        exit={{ scale: 0, opacity: 0 }}
                        transition={{ type: 'spring', damping: 15 }}
                        style={{
                            position: 'fixed',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            backgroundColor: theme.danger,
                            padding: isMobile ? '20px 30px' : '40px 60px',
                            borderRadius: '20px',
                            border: `5px solid ${theme.light}`,
                            zIndex: 2000,
                            boxShadow: '0 10px 40px rgba(0,0,0,0.5)'
                        }}
                    >
                        <h1 style={{
                            fontSize: isMobile ? '2.5rem' : '4rem',
                            margin: 0,
                            color: 'white',
                            textShadow: '3px 3px 6px rgba(0,0,0,0.5)'
                        }}>
                            UNO!
                        </h1>
                        <p style={{
                            fontSize: isMobile ? '1rem' : '1.5rem',
                            margin: '10px 0 0 0',
                            color: 'white'
                        }}>
                            {unoPlayer} has one card left!
                        </p>
                    </motion.div>
                )}
            </AnimatePresence>

            <AnimatePresence>
                {showWinnerCard && (
                    <motion.div
                        initial={{ scale: 0.6, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        exit={{ scale: 0.6, opacity: 0 }}
                        transition={{ type: 'spring', damping: 18 }}
                        style={{
                            position: 'fixed',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            backgroundColor: theme.success,
                            padding: isMobile ? '24px 28px' : '40px 54px',
                            borderRadius: '20px',
                            border: `5px solid ${theme.light}`,
                            zIndex: 2100,
                            boxShadow: '0 10px 40px rgba(0,0,0,0.5)',
                            textAlign: 'center'
                        }}
                    >
                        <h1 style={{
                            margin: 0,
                            fontSize: isMobile ? '2rem' : '3rem',
                            color: theme.textPrimary
                        }}>
                            Winner
                        </h1>
                        <p style={{
                            margin: '12px 0 0 0',
                            fontSize: isMobile ? '1rem' : '1.4rem',
                            color: theme.textPrimary
                        }}>
                            {winnerName} wins!
                        </p>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* PHASE 3: Hand Rotation Popup */}
            <AnimatePresence>
                {showHandRotationMessage && (
                    <motion.div
                        initial={{ scale: 0, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        exit={{ scale: 0, opacity: 0 }}
                        transition={{ type: 'spring', damping: 15 }}
                        style={{
                            position: 'fixed',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            backgroundColor: theme.accent,
                            padding: isMobile ? '20px 30px' : '40px 60px',
                            borderRadius: '20px',
                            border: `5px solid ${theme.light}`,
                            zIndex: 2000,
                            boxShadow: '0 10px 40px rgba(0,0,0,0.5)'
                        }}
                    >
                        <div style={{ marginBottom: '6px', color: 'white' }}>
                            <ShuffleIcon size={isMobile ? 44 : 58} />
                        </div>
                        <p style={{
                            fontSize: isMobile ? '1rem' : '1.5rem',
                            margin: '10px 0 0 0',
                            color: 'white'
                        }}>
                            All hands rotated {handRotationDirection}!
                        </p>
                    </motion.div>
                )}
            </AnimatePresence>

            <AnimatePresence>
                {showEliminatedMessage && (
                    <motion.div
                        initial={{ scale: 0, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        exit={{ scale: 0, opacity: 0 }}
                        transition={{ type: 'spring', damping: 15 }}
                        style={{
                            position: 'fixed',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            backgroundColor: '#2C3E50',
                            padding: isMobile ? '20px 30px' : '40px 60px',
                            borderRadius: '20px',
                            border: `5px solid ${theme.textDisabled}`,
                            zIndex: 2000,
                            boxShadow: '0 10px 40px rgba(0,0,0,0.5)'
                        }}
                    >
                        <div style={{ marginBottom: '6px', color: 'white' }}>
                            <SkullIcon size={isMobile ? 42 : 56} />
                        </div>
                        <p style={{
                            fontSize: isMobile ? '1rem' : '1.5rem',
                            margin: '10px 0 0 0',
                            color: 'white'
                        }}>
                            {eliminatedMessage} ELIMINATED!
                        </p>
                        <p style={{
                            fontSize: isMobile ? '0.8rem' : '1rem',
                            margin: '5px 0 0 0',
                            color: theme.textDisabled
                        }}>
                            Mercy Rule: 25+ cards
                        </p>
                    </motion.div>
                )}
            </AnimatePresence>

            <AnimatePresence>
                {showUnplayableCards && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        style={{
                            position: 'fixed',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundColor: 'rgba(0, 0, 0, 0.8)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            zIndex: 1500,
                            padding: '20px'
                        }}
                        onClick={() => setShowUnplayableCards(false)}
                    >
                        <motion.div
                            initial={{ scale: 0.8, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.8, opacity: 0 }}
                            onClick={(e) => e.stopPropagation()}
                            style={{
                                backgroundColor: theme.bgMedium,
                                padding: isMobile ? '20px' : '30px',
                                borderRadius: '15px',
                                border: `3px solid ${theme.light}`,
                                maxWidth: '90vw',
                                maxHeight: '80vh',
                                overflow: 'auto',
                                position: 'relative'
                            }}
                        >
                            <button
                                onClick={() => setShowUnplayableCards(false)}
                                style={{
                                    position: 'absolute',
                                    top: '10px',
                                    right: '10px',
                                    background: 'none',
                                    border: 'none',
                                    color: theme.textPrimary,
                                    fontSize: '1.5rem',
                                    cursor: 'pointer',
                                    padding: '5px 10px'
                                }}
                            >
                                ✕
                            </button>

                            <h2 style={{
                                marginBottom: '20px',
                                textAlign: 'center',
                                fontSize: isMobile ? '1.2rem' : '1.5rem',
                                color: theme.textPrimary
                            }}>
                                Unplayable Cards ({unplayableCards.length})
                            </h2>

                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: isMobile ? 'repeat(auto-fill, minmax(60px, 1fr))' : 'repeat(auto-fill, minmax(80px, 1fr))',
                                gap: isMobile ? '10px' : '15px',
                                justifyItems: 'center'
                            }}>
                                {unplayableCards.map(({ card, originalIndex }) => (
                                    <UnoCard
                                        key={originalIndex}
                                        card={card}
                                        isPlayable={false}
                                        size="small"
                                    />
                                ))}
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* PHASE 3: Color Picker with X button */}
            {showColorPicker && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.8)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1000,
                    padding: '20px'
                }} onClick={closeColorPicker}>
                    <div style={{
                        backgroundColor: theme.bgMedium,
                        padding: isMobile ? '20px' : '40px',
                        borderRadius: '15px',
                        border: `3px solid ${theme.light}`,
                        maxWidth: '90vw',
                        position: 'relative'
                    }} onClick={(e) => e.stopPropagation()}>
                        {/* PHASE 3: X button */}
                        <button
                            onClick={closeColorPicker}
                            style={{
                                position: 'absolute',
                                top: '10px',
                                right: '10px',
                                background: 'none',
                                border: 'none',
                                color: theme.textPrimary,
                                fontSize: '1.5rem',
                                cursor: 'pointer',
                                padding: '5px 10px',
                                zIndex: 10
                            }}
                        >
                            ✕
                        </button>

                        <h2 style={{
                            marginBottom: isMobile ? '20px' : '30px',
                            textAlign: 'center',
                            fontSize: isMobile ? '1.2rem' : '1.5rem'
                        }}>
                            Choose a Color
                        </h2>
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: '1fr 1fr',
                            gap: isMobile ? '10px' : '20px'
                        }}>
                            {['red', 'yellow', 'green', 'blue'].map(color => (
                                <motion.button
                                    key={color}
                                    whileHover={{ scale: 1.1 }}
                                    whileTap={{ scale: 0.95 }}
                                    onClick={() => handleColorChoice(color)}
                                    style={{
                                        width: isMobile ? '80px' : '120px',
                                        height: isMobile ? '80px' : '120px',
                                        borderRadius: '15px',
                                        border: '4px solid white',
                                        backgroundColor: {
                                            red: '#E74C3C',
                                            yellow: '#F39C12',
                                            green: '#27AE60',
                                            blue: '#3498DB'
                                        }[color],
                                        cursor: 'pointer',
                                        fontSize: isMobile ? '1rem' : '1.3rem',
                                        fontWeight: 'bold',
                                        color: 'white',
                                        fontFamily: 'inherit',
                                        textTransform: 'capitalize',
                                        textShadow: '2px 2px 4px rgba(0,0,0,0.5)'
                                    }}
                                >
                                    {color}
                                </motion.button>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {showRouletteColorPicker && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.8)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1000,
                    padding: '20px'
                }}>
                    <div style={{
                        backgroundColor: theme.bgMedium,
                        padding: isMobile ? '20px' : '40px',
                        borderRadius: '15px',
                        border: `3px solid ${theme.light}`,
                        maxWidth: '90vw'
                    }}>
                        <h2 style={{
                            marginBottom: isMobile ? '20px' : '30px',
                            textAlign: 'center',
                            fontSize: isMobile ? '1.2rem' : '1.5rem',
                            color: theme.danger
                        }}>
                            🎰 Color Roulette! 🎰
                        </h2>
                        <p style={{
                            textAlign: 'center',
                            marginBottom: '20px',
                            fontSize: isMobile ? '0.9rem' : '1rem',
                            color: theme.textSecondary
                        }}>
                            Choose a color - you'll draw cards until you get it!<br />
                            (Wild cards don't count)
                        </p>
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: '1fr 1fr',
                            gap: isMobile ? '10px' : '20px'
                        }}>
                            {['red', 'yellow', 'green', 'blue'].map(color => (
                                <motion.button
                                    key={color}
                                    whileHover={{ scale: 1.1 }}
                                    whileTap={{ scale: 0.95 }}
                                    onClick={() => handleRouletteColorChoice(color)}
                                    style={{
                                        width: isMobile ? '80px' : '120px',
                                        height: isMobile ? '80px' : '120px',
                                        borderRadius: '15px',
                                        border: '4px solid white',
                                        backgroundColor: {
                                            red: '#E74C3C',
                                            yellow: '#F39C12',
                                            green: '#27AE60',
                                            blue: '#3498DB'
                                        }[color],
                                        cursor: 'pointer',
                                        fontSize: isMobile ? '1rem' : '1.3rem',
                                        fontWeight: 'bold',
                                        color: 'white',
                                        fontFamily: 'inherit',
                                        textTransform: 'capitalize',
                                        textShadow: '2px 2px 4px rgba(0,0,0,0.5)'
                                    }}
                                >
                                    {color}
                                </motion.button>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {showPlayerPicker && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.8)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1000,
                    padding: '20px'
                }}>
                    <div style={{
                        backgroundColor: theme.bgMedium,
                        padding: isMobile ? '20px' : '40px',
                        borderRadius: '15px',
                        border: `3px solid ${theme.light}`,
                        maxWidth: '90vw'
                    }}>
                        <h2 style={{
                            marginBottom: isMobile ? '20px' : '30px',
                            textAlign: 'center',
                            fontSize: isMobile ? '1.2rem' : '1.5rem'
                        }}>
                            Swap Hands With?
                        </h2>
                        <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: isMobile ? '10px' : '15px'
                        }}>
                            {game.players.map((player, idx) => {
                                if (idx === game.currentPlayerIndex || player.isEliminated) return null;
                                return (
                                    <motion.button
                                        key={idx}
                                        whileHover={{ scale: 1.05 }}
                                        whileTap={{ scale: 0.95 }}
                                        onClick={() => handlePlayerChoice(idx)}
                                        style={{
                                            padding: isMobile ? '15px' : '20px',
                                            borderRadius: '10px',
                                            border: `2px solid ${theme.accent}`,
                                            backgroundColor: theme.bgDark,
                                            cursor: 'pointer',
                                            fontSize: isMobile ? '1rem' : '1.2rem',
                                            fontWeight: 'bold',
                                            color: theme.textPrimary,
                                            fontFamily: 'inherit'
                                        }}
                                    >
                                        {player.name} ({player.hand.length} cards)
                                    </motion.button>
                                );
                            })}
                        </div>
                    </div>
                </div>
            )}

            {showDiscardAllPicker && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.8)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1000,
                    padding: '20px'
                }}>
                    <div style={{
                        backgroundColor: theme.bgMedium,
                        padding: isMobile ? '20px' : '40px',
                        borderRadius: '15px',
                        border: `3px solid ${theme.light}`,
                        maxWidth: '90vw'
                    }}>
                        <h2 style={{
                            marginBottom: isMobile ? '20px' : '30px',
                            textAlign: 'center',
                            fontSize: isMobile ? '1.2rem' : '1.5rem'
                        }}>
                            Discard All Cards of Which Color?
                        </h2>
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: '1fr 1fr',
                            gap: isMobile ? '10px' : '20px'
                        }}>
                            {['red', 'yellow', 'green', 'blue'].map(color => {
                                const count = activeHandPlayer.hand.filter(c => c.color === color).length;
                                return (
                                    <motion.button
                                        key={color}
                                        whileHover={{ scale: 1.1 }}
                                        whileTap={{ scale: 0.95 }}
                                        onClick={() => handleDiscardAllChoice(color)}
                                        disabled={count === 0}
                                        style={{
                                            width: isMobile ? '80px' : '120px',
                                            height: isMobile ? '80px' : '120px',
                                            borderRadius: '15px',
                                            border: '4px solid white',
                                            backgroundColor: {
                                                red: '#E74C3C',
                                                yellow: '#F39C12',
                                                green: '#27AE60',
                                                blue: '#3498DB'
                                            }[color],
                                            cursor: count > 0 ? 'pointer' : 'not-allowed',
                                            fontSize: isMobile ? '0.9rem' : '1.1rem',
                                            fontWeight: 'bold',
                                            color: 'white',
                                            fontFamily: 'inherit',
                                            textTransform: 'capitalize',
                                            textShadow: '2px 2px 4px rgba(0,0,0,0.5)',
                                            opacity: count === 0 ? 0.5 : 1,
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: '5px'
                                        }}
                                    >
                                        <div>{color}</div>
                                        <div style={{ fontSize: '0.8rem' }}>({count})</div>
                                    </motion.button>
                                );
                            })}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default UnoNMGame;
